import {
  Box,
  useTheme,
  TextField,
  Typography,
  Button,
  IconButton,
  Autocomplete,
  InputAdornment,
  Divider,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid2";

import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import { getBorderColor } from "scenes/services/interpretations/InterpretationNewEdit";
import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import Overlay from "components/Overlay/Overlay";

const RateNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const { overlayData, setOverlayName, triggerPageRefresh } = useStateContext();
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const isNewRate = overlayData.Action === OVERLAY_NAMES.RATE_ACTION_NEW;

  // Edit lock using ClientID as reference (each client has its rates edited in one place)
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "clientServiceRate",
    overlayData.ClientID,
  );

  // Existing per-item languages list
  const [languages] = useState(overlayData.Languages);

  // Default "ALL" rate
  const [defaultRate, setDefaultRate] = useState(
    overlayData.ClientRates.find(
      (clientRate) => clientRate.Language.Name === "ALL",
    )?.Rate ?? 0,
  );

  // Exceptions list (non-ALL entries)
  const [clientRates, setClientRates] = useState(() =>
    (overlayData.ClientRates || [])
      .filter((clientRate) => clientRate?.Language?.Name !== "ALL")
      .map((clientRate) => ({
        Language: clientRate?.Language ?? null,
        Rate: clientRate?.Rate ?? "",
        id: clientRate?.id ?? 0,
      })),
  );

  const removeRate = (rateToRemove) => {
    setClientRates((prevRates) =>
      prevRates.filter((rate) => rate.id !== rateToRemove.id),
    );
  };

  // Helper to update a single row by id and field
  const handleClientRateChange = (clientRateId, field, value) => {
    setClientRates((prevClientRates) =>
      prevClientRates.map((clientRate) => {
        if (clientRate.id !== clientRateId) return clientRate;

        if (field === "Language") {
          return {
            ...clientRate,
            Language: value || { Name: "", id: 0 },
          };
        }

        if (field === "Rate") {
          const nextVal =
            value === "" || value === null || value === undefined
              ? ""
              : Number(value);
          return {
            ...clientRate,
            Rate: nextVal,
          };
        }

        return clientRate;
      }),
    );
  };

  const saveRate = useCallback(async () => {
    try {
      //setIsSavingChanges(true);
      //setIsEditable(false);

      // Example payload: adjust endpoint/shape to your backend

      const newRatesList = [
        ...clientRates.map((clientRate) => ({
          ClientID: overlayData.ClientID,
          LanguageID: clientRate.Language?.id ?? 0,
          Rate: Number(clientRate.Rate),
        })),
        {
          ClientID: overlayData.ClientID,
          LanguageID: 1,
          Rate: Number(defaultRate),
        },
      ];

      await api.post("/clients/updateRates", { data: newRatesList });

      const clientId = overlayData?.ClientID;
      // Always show as "Updated" to keep snackbar wording consistent
      const changeAction = "Updated";

      if (socket && clientId) {
        socket.emit("entity:saved", {
          modelType: "Client",
          recordId: clientId,
          changeType: "Rate",
          changeAction,
          user: {
            id: currentUser?.id,
            name: currentUser?.Name || "User",
            imageThumbnail: currentUser?.ImageThumbnail,
          },
        });
      }

      triggerPageRefresh("Rates saved successfully!", theme);
      setOverlayName("");
    } catch (err) {
      console.log(err);
      setIsSavingChanges(false);
      setIsEditable(true);
    }
  }, [
    clientRates,
    currentUser?.ImageThumbnail,
    currentUser?.Name,
    currentUser?.id,
    defaultRate,
    overlayData?.ClientID,
    setOverlayName,
    socket,
    theme,
    triggerPageRefresh,
  ]);

  const hasInvalidRates = useCallback(
    () =>
      clientRates.some(
        (cr) =>
          !cr?.Language?.Name ||
          cr?.Language?.id === 0 ||
          cr?.Rate === "" ||
          Number(cr?.Rate) === 0,
      ),
    [clientRates],
  );

  const attemptSave = useCallback(() => {
    if (isSavingChanges || isLocked || hasInvalidRates()) {
      return;
    }

    setIsSavingChanges(true);
    saveRate();
  }, [hasInvalidRates, isLocked, isSavingChanges, saveRate]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog
      ) {
        e.preventDefault();
        attemptSave();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [attemptSave]);

  const footer = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        width: "100%",
        flexDirection: "row",
        p: ".75rem 2rem",
      }}
    >
      {isMobile ? (
        <Tooltip title="Discard" placement="top">
          <IconButton
            color="primary"
            onClick={() => setOverlayName("")}
            sx={{
              width: "40px",
              height: "40px",
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "12px",
            }}
          >
            <DisabledByDefaultRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          color="primary"
          variant="outlined"
          size="large"
          endIcon={<DisabledByDefaultRoundedIcon />}
          onClick={() => setOverlayName("")}
          sx={{
            borderRadius: "12px",
          }}
        >
          Discard
        </Button>
      )}

      <Box p="0 .5rem" />

      {isMobile ? (
        <Tooltip title="Save" placement="top">
          <span>
            <IconButton
              disabled={
                isSavingChanges ||
                isLocked ||
                hasInvalidRates()
              }
              color="primary"
              onClick={attemptSave}
              sx={{
                width: "40px",
                height: "40px",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.default,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&.Mui-disabled": {
                  backgroundColor:
                    theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              <SaveRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          loading={isSavingChanges}
          color="primary"
          variant="contained"
          size="large"
          endIcon={<SaveRoundedIcon />}
          type="submit"
          onClick={() => {
            attemptSave();
          }}
          disabled={
            isSavingChanges ||
            isLocked ||
            hasInvalidRates()
          }
          sx={{
            borderRadius: "12px",
          }}
        >
          Save
        </Button>
      )}
    </Box>
  );

  return (
    <>
      <Overlay
        id="rate-new-edit"
        open={true}
        onClose={() => setOverlayName("")}
        title="Update Interpretation Rates"
        footer={footer}
      >
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            p: "1rem 2.3rem 1rem 2.5rem",
            width: "100%",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2} width="100%" flexShrink={0}>
            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"DefaultRate"}
                label="Default Rate"
                type="number"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                value={defaultRate}
                onChange={(event) => {
                  setDefaultRate(event.target.value);
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography variant="h4" fontWeight="bold" margin="0 1rem 0 0">
                  Exceptions
                </Typography>
                <IconButton
                  onClick={() => {
                    setClientRates((prevAvailabilities) => [
                      ...prevAvailabilities,
                      {
                        id:
                          clientRates.length === 0
                            ? 1
                            : clientRates.at(-1).id + 1,
                        Language: { Name: "", id: 0 },
                        Rate: 0,
                      },
                    ]);
                  }}
                >
                  <AddBoxRoundedIcon
                    sx={{
                      fontSize: 24,
                      color: theme.palette.primary.main,
                    }}
                  />
                </IconButton>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              width: "100%",
              mt: 1,
              px: 1,
            }}
          >
            <Grid container spacing={2} width="100%">
              {clientRates.map((clientRate, index) => {
                return (
                  <Grid
                    key={clientRate?.id + "--" + index}
                    container
                    spacing={2}
                    bgcolor={theme.palette.background.default}
                    borderRadius="12px"
                    p=".6rem"
                    size={{ xs: 12 }}
                  >
                    <Grid size={{ xs: 12, md: 5.7 }}>
                      <Autocomplete
                        disabled={!isEditable}
                        disableClearable
                        key={clientRate?.id + "-+-" + index}
                        size="small"
                        options={(languages || []).filter(
                          (language) =>
                            !(clientRates || []).some(
                              (clientRate) =>
                                clientRate.Language?.Name === language.Name,
                            ),
                        )}
                        getOptionLabel={(language) => language?.Name || ""}
                        value={
                          clientRate?.Language?.id ? clientRate.Language : null
                        }
                        onChange={(event, newValue) => {
                          handleClientRateChange(
                            clientRate.id,
                            "Language",
                            newValue,
                          );
                        }}
                        selectOnFocus
                        clearOnBlur
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Language"
                            placeholder=""
                            error={
                              !clientRate?.Language?.Name ||
                              clientRate?.Language?.id === 0
                            }
                          />
                        )}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&.MuiInputBase-root fieldset": {
                              borderColor: getBorderColor(
                                theme,
                                isNewRate,
                                (clientRate &&
                                  clientRate?.Language?.Name !== "") ||
                                  "",
                                "",
                              ),
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 5.7 }}>
                      <TextField
                        disabled={!isEditable}
                        size="small"
                        id={"Rate"}
                        label="Rate"
                        type="number"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& .MuiInputBase-input": {
                              padding: "8.5px 12px !important",
                            },
                            "&.MuiInputBase-root fieldset": {
                              borderColor: getBorderColor(
                                theme,
                                isNewRate,
                                (clientRate && clientRate?.Rate !== 0) || "",
                                "",
                              ),
                            },
                          },
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          },
                        }}
                        value={clientRate?.Rate}
                        onChange={(event) => {
                          handleClientRateChange(
                            clientRate.id,
                            "Rate",
                            event.target.value,
                          );
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 0.5 }}>
                      <IconButton
                        disabled={clientRate?.Language?.Name === "Spanish"}
                        onClick={() => removeRate(clientRate)}
                      >
                        <DeleteForeverRoundedIcon
                          sx={{
                            fontSize: 24,
                            color:
                              clientRate?.Language?.Name === "Spanish"
                                ? ""
                                : theme.palette.primary.main,
                          }}
                        />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default RateNewEdit;
